/**
 * Utility to fetch place details using the new Google Maps Places API
 * This replaces the deprecated PlacesService.getDetails() method
 *
 * @see https://developers.google.com/maps/documentation/javascript/places-migration-autocomplete
 */

/**
 * Interface matching the legacy PlaceResult structure for backward compatibility
 */
export interface PlaceDetailsCompat {
  place_id: string
  name?: string
  formatted_address?: string
  geometry?: {
    location: {
      lat: () => number
      lng: () => number
    }
    viewport?: google.maps.LatLngBounds
  }
  address_components?: google.maps.GeocoderAddressComponent[]
  types?: string[]
  // Additional fields from the new API
  displayName?: string
  formattedAddress?: string
}

/**
 * Available fields for place details
 * Maps to the new API field names
 */
export type PlaceDetailsField =
  | 'displayName'
  | 'formattedAddress'
  | 'location'
  | 'viewport'
  | 'addressComponents'
  | 'types'
  | 'id'
  | 'photos'
  | 'plusCode'
  | 'utcOffsetMinutes'

/**
 * Default fields to fetch if none specified
 */
const DEFAULT_FIELDS: PlaceDetailsField[] = [
  'displayName',
  'formattedAddress',
  'location',
  'viewport',
  'addressComponents',
  'types'
]

/**
 * Fetch place details using the new Google Maps Places API
 *
 * @param placeId - The place ID to fetch details for
 * @param fields - Optional array of fields to fetch (defaults to common fields)
 * @returns Promise resolving to place details in a backward-compatible format
 */
export async function getPlaceDetails(
  placeId: string,
  fields: PlaceDetailsField[] = DEFAULT_FIELDS
): Promise<PlaceDetailsCompat | null> {
  if (!placeId) {
    console.warn('getPlaceDetails: placeId is required')
    return null
  }

  // Check if the new API is available
  if (typeof google === 'undefined' || !google.maps?.places?.Place) {
    console.warn('Google Maps Places API (new) not available')
    return null
  }

  try {
    // Create a Place instance with the place ID
    const place = new google.maps.places.Place({
      id: placeId
    })

    // Fetch the requested fields
    await place.fetchFields({
      fields: fields as string[]
    })

    // Convert to backward-compatible format
    const result: PlaceDetailsCompat = {
      place_id: placeId,
      displayName: place.displayName || undefined,
      formattedAddress: place.formattedAddress || undefined
    }

    // Map displayName to name for legacy compatibility
    if (place.displayName) {
      result.name = place.displayName
    }

    // Map formattedAddress for legacy compatibility
    if (place.formattedAddress) {
      result.formatted_address = place.formattedAddress
    }

    // Map location to geometry
    if (place.location) {
      result.geometry = {
        location: {
          lat: () => place.location!.lat(),
          lng: () => place.location!.lng()
        },
        viewport: place.viewport || undefined
      }
    }

    // Map address components
    if (place.addressComponents) {
      result.address_components = place.addressComponents.map(component => ({
        long_name: component.longText || '',
        short_name: component.shortText || '',
        types: component.types || []
      }))
    }

    // Map types
    if (place.types) {
      result.types = place.types
    }

    return result
  } catch (error) {
    console.error('Error fetching place details:', error)
    return null
  }
}

/**
 * Callback-style wrapper for getPlaceDetails for easier migration
 * This provides a similar interface to the legacy placesService.getDetails()
 *
 * @param request - Object containing placeId
 * @param callback - Callback function receiving place details
 */
export function getPlaceDetailsCallback(
  request: { placeId: string; fields?: PlaceDetailsField[] },
  callback: (placeDetails: PlaceDetailsCompat | null) => void
): void {
  getPlaceDetails(request.placeId, request.fields)
    .then(callback)
    .catch(error => {
      console.error('Error in getPlaceDetailsCallback:', error)
      callback(null)
    })
}

export default getPlaceDetails
